import axios from 'axios'
import { userType, salesRepType, customerType, salesOrderLineType } from './types'
import { DatePicker as MuiDatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { SxProps, TextField, Button, Typography, Menu, MenuItem, Chip, Tooltip, IconButton } from '@mui/material'
import { Dayjs } from 'dayjs'
import { useContext, useState, useRef, useEffect } from 'react'
import './helper.css'
import { dataContext } from '.'
import { PersonAddAlt1Outlined } from '@mui/icons-material'

export async function getSalesReps() {
  const res = await axios.get('/salesReps')
  return res.data.sort((a: salesRepType, b: salesRepType) => a.initial.localeCompare(b.initial))
}

export async function getOperations() {
  const res = await axios.get('/operations')
  return res.data.sort((a: userType, b: userType) => a.display_name?.localeCompare(b.display_name ?? '') ?? 0)
}

export async function getCustomers(): Promise<customerType[]> {
  const res = await axios.get('/customers')
  return res.data.sort((a: customerType, b: customerType) => a.name.localeCompare(b.name))
}

export async function getProfile(email: string): Promise<userType> {
  try {
    const res = await axios.get(`/profile/${email}`)

    if (!res.data) {
      return {
        user_id: '',
        user_type: 'Office Staff',
        display_name: 'Guest',
        email: email,
        active_flag: true,
      }
    }

    return {
      user_id: res.data.user_id,
      user_type: res.data.user_type,
      display_name: res.data.display_name,
      email: res.data.email,
      active_flag: res.data.active_flag,
    }
  } catch (error) {
    console.error(error)
    return {
      user_id: '',
      user_type: 'Office Staff',
      display_name: 'Guest',
      email: email,
      active_flag: true,
    }
  }
}

export function DatePicker(props: {
  label?: string
  sx?: SxProps
  size?: 'small' | 'medium'
  value?: Dayjs | null
  disabled?: boolean
  className?: string
  onChange?: (date: Dayjs | null) => void
}) {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <MuiDatePicker
        timezone="UTC"
        label={props.label}
        sx={props.sx}
        slotProps={{ textField: { size: props.size, className: props.className } }}
        value={props.value}
        onChange={props.onChange}
        disabled={props.disabled}
      />
    </LocalizationProvider>
  )
}

export function NumberField(props: {
  label: string
  size?: 'small' | 'medium'
  value?: number | null
  onChange?: (value: number | null) => void
  fullWidth?: boolean
  className?: string
}) {
  return (
    <TextField
      fullWidth={props.fullWidth}
      label={props.label}
      className={props.className}
      size={props.size}
      value={props.value ?? ''}
      onChange={e => {
        if (e.target.value.match(/^[\d]+$/)) {
          props.onChange?.(Number(e.target.value))
        } else if (e.target.value === '') {
          props.onChange?.(null)
        }
      }}
    />
  )
}

export function Expandable(props: { text: string | null; maxHeight?: number; hideExpandable?: boolean; gap?: number }) {
  const [expanded, setExpanded] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const [hasOverflowed, setHasOverflowed] = useState(false)
  const textRef = useRef<HTMLDivElement>(null)

  const lines = props.text?.split('\n') || []

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current && props.maxHeight && props.maxHeight !== -1 && props.text) {
        // Check for explicit line breaks first
        const hasExplicitLineBreaks = lines.length > props.maxHeight

        // Check if content is actually overflowing by comparing scrollHeight to clientHeight
        const scrollHeight = textRef.current.scrollHeight
        const clientHeight = textRef.current.clientHeight
        const isActuallyOverflowing = scrollHeight > clientHeight

        // Determine if content should show button
        const shouldShowButton = hasExplicitLineBreaks || isActuallyOverflowing

        // Update overflow state based on current conditions
        if (shouldShowButton && !hasOverflowed) {
          setHasOverflowed(true)
        } else if (!shouldShowButton && hasOverflowed && !expanded) {
          // Only reset if content is not currently expanded
          setHasOverflowed(false)
        }

        // Show button if currently needed OR if content was expanded and we've detected overflow before
        setIsOverflowing(shouldShowButton || (hasOverflowed && expanded))
      }
    }

    // Check immediately and after delays
    checkOverflow()
    setTimeout(checkOverflow, 100)
    setTimeout(checkOverflow, 500)

    // Add resize listener
    window.addEventListener('resize', checkOverflow)

    return () => {
      window.removeEventListener('resize', checkOverflow)
    }
  }, [props.text, props.maxHeight, expanded, lines.length, hasOverflowed])

  return (
    <div className="responsive-expandable-container" style={{ gap: props.gap ? `${props.gap}px` : '4px' }}>
      <div
        ref={textRef}
        className={`responsive-expandable-text ${expanded ? 'expanded' : 'collapsed'}`}
        style={{
          gap: props.gap ? `${props.gap}px` : '0px',
          maxHeight: expanded
            ? 'none'
            : props.maxHeight
            ? props.maxHeight === -1
              ? 'none'
              : `${props.maxHeight * 1.43}em`
            : '8.5em',
        }}
      >
        {lines.map((line, index) => (
          <Typography key={index} variant="body2">
            {line}
          </Typography>
        ))}
      </div>
      {!props.hideExpandable && props.text && props.maxHeight !== -1 && isOverflowing && (
        <Button
          size="small"
          className="responsive-expandable-button"
          onClick={() => {
            setExpanded(!expanded)
          }}
        >
          {expanded ? 'See Less' : 'See More'}
        </Button>
      )}
    </div>
  )
}

export function OpsMenu(props: {
  open: boolean
  objectId: string
  anchorEl: HTMLElement | null
  onClose: () => void
  setOpen: (open: boolean) => void
  setOwner: (objectId: string, owner: string) => void
}) {
  const { operations } = useContext(dataContext)

  return (
    <Menu
      open={props.open}
      anchorEl={props.anchorEl}
      onClose={props.onClose}
      title="Assign Operations Owner"
      slotProps={{
        paper: {
          style: {
            maxHeight: 350,
          },
        },
      }}
    >
      {operations.map(user => (
        <MenuItem
          key={user.user_id}
          onClick={() => {
            props.setOwner(props.objectId, user.user_id)
            props.setOpen(false)
          }}
        >
          {user.display_name}
        </MenuItem>
      ))}
    </Menu>
  )
}

export function OpsOwner(props: {
  operationsOwner: string | null
  objectId: string
  handleEditOpsOwner: (objectId: string, owner: string | null) => void
}) {
  const { operations, user } = useContext(dataContext)
  const [opsMenuOpen, setOpsMenuOpen] = useState(false)
  const anchorEl = useRef<HTMLButtonElement>(null)

  if (props.operationsOwner) {
    return (
      <div>
        <Tooltip
          title={operations.find(operation => operation.user_id === props.operationsOwner)?.display_name}
          placement="top"
        >
          <Chip
            label={operations.find(operation => operation.user_id === props.operationsOwner)?.display_name}
            size="medium"
            onClick={
              user.user_type !== 'Office Staff' && user.user_type !== 'Sales Rep'
                ? () => {
                    setOpsMenuOpen(true)
                    anchorEl.current = document.activeElement as HTMLButtonElement
                  }
                : undefined
            }
            onDelete={
              user.user_type !== 'Office Staff' && user.user_type !== 'Sales Rep'
                ? () => {
                    if (props.objectId) {
                      props.handleEditOpsOwner(props.objectId, null)
                      setOpsMenuOpen(false)
                    }
                  }
                : undefined
            }
          />
        </Tooltip>
        <OpsMenu
          open={opsMenuOpen}
          objectId={props.objectId}
          anchorEl={anchorEl.current}
          onClose={() => setOpsMenuOpen(false)}
          setOpen={open => setOpsMenuOpen(open)}
          setOwner={props.handleEditOpsOwner}
        />
      </div>
    )
  }

  return (
    <div>
      <IconButton
        title="Assign Operations Owner"
        onClick={
          user.user_type !== 'Office Staff' && user.user_type !== 'Sales Rep'
            ? e => {
                setOpsMenuOpen(true)
                anchorEl.current = e.currentTarget
              }
            : undefined
        }
      >
        <PersonAddAlt1Outlined color="action" />
      </IconButton>
      <OpsMenu
        open={opsMenuOpen}
        objectId={props.objectId}
        anchorEl={anchorEl.current}
        onClose={() => setOpsMenuOpen(false)}
        setOpen={open => {
          setOpsMenuOpen(open)
        }}
        setOwner={props.handleEditOpsOwner}
      />
    </div>
  )
}

export function getRepCodeName(repCode: salesRepType | undefined) {
  if (!repCode) return ''

  return [
    repCode.account_executive?.split(' ')[0],
    repCode.engineer?.split(' ')[0],
    repCode.inside_sales_rep.split(' ')[0],
  ]
    .filter(Boolean)
    .join(', ')
}

export function formatAddress(address: Partial<salesOrderLineType>) {
  const cityStateZip = [
    address.ship_address_city,
    [address.ship_address_state, address.ship_address_zip].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ')

  return [
    address.ship_address_attention,
    address.ship_address_addressee,
    address.ship_address_addr1,
    address.ship_address_addr2,
    cityStateZip,
  ]
    .filter((line): line is string => typeof line === 'string' && line.trim() !== '')
    .join('\n')
}
